import { adminOperationsRepository, AdminOperationsRepository } from '@/infrastructure/repositories/AdminOperationsRepository';
import {
  ManualImportResultDto,
  RedditCrawlStatusDto
} from '@/application/dto/AdminOperationsDtos';

/**
 * AdminOperationsService - Application service for admin manual operations
 *
 * Orchestrates manual OSM imports and Reddit crawl control
 * All operations are admin-only (enforced by backend)
 */
export class AdminOperationsService {
  constructor(
    private readonly repository: AdminOperationsRepository = adminOperationsRepository
  ) {}

  /**
   * Trigger manual OSM import for a SubArea or all SubAreas in a region
   *
   * @param subAreaId - GUID of the SubArea to import data for (single SubArea import), null for all SubAreas
   * @param regionId - GUID of the Region to import all SubAreas for (multi-SubArea import), optional
   * @returns Promise<ManualImportResultDto> - Import result with statistics
   * @throws Error if import fails or validation errors
   */
  async triggerOsmImport(
    subAreaId: string | null,
    regionId?: string
  ): Promise<ManualImportResultDto> {
    // Validate that EITHER subAreaId OR regionId is provided
    if (!subAreaId && !regionId) {
      throw new Error('Must provide either SubAreaId or RegionId');
    }

    if (subAreaId && regionId) {
      throw new Error('Cannot provide both SubAreaId and RegionId - please specify only one');
    }

    // Validate GUID formats
    if (subAreaId && !this.isValidGuid(subAreaId)) {
      throw new Error('Invalid SubArea ID format');
    }

    if (regionId && !this.isValidGuid(regionId)) {
      throw new Error('Invalid Region ID format');
    }

    return this.repository.triggerOsmImport(subAreaId, regionId);
  }

  /**
   * Start Reddit crawl manually
   *
   * Note: Placeholder for Sessions 005/006
   * @returns Promise<void>
   * @throws Error if crawl start fails
   */
  async startRedditCrawl(): Promise<void> {
    return this.repository.startRedditCrawl();
  }

  /**
   * Stop Reddit crawl manually
   *
   * Note: Placeholder for Sessions 005/006
   * @returns Promise<void>
   * @throws Error if crawl stop fails
   */
  async stopRedditCrawl(): Promise<void> {
    return this.repository.stopRedditCrawl();
  }

  /**
   * Get current Reddit crawl status
   *
   * Note: Placeholder for Sessions 005/006
   * @returns Promise<RedditCrawlStatusDto> - Current crawl status
   * @throws Error if status fetch fails
   */
  async getRedditCrawlStatus(): Promise<RedditCrawlStatusDto> {
    return this.repository.getRedditCrawlStatus();
  }

  /**
   * Get the current overall Reddit crawler status with health metrics
   *
   * @returns Promise with crawler status and statistics
   * @throws Error if status fetch fails
   */
  async getCrawlStatus(): Promise<any> {
    return this.repository.getCrawlStatus();
  }

  /**
   * Get recent crawl history records with pagination and filtering
   * Sprint 027 SESSION_358: Added pagination (page, pageSize) and subreddit filter.
   *
   * @param hours - Number of hours to look back (default: 24)
   * @param maxResults - Maximum number of results to return (default: 50) - DEPRECATED, use pageSize
   * @param page - Page number for pagination (default: 1)
   * @param pageSize - Number of items per page (default: 50)
   * @param subredditId - Optional filter by subreddit ID
   * @returns Promise with paginated crawl history records
   * @throws Error if fetch fails
   */
  async getCrawlHistory(
    hours: number = 24,
    maxResults: number = 50,
    page: number = 1,
    pageSize: number = 50,
    subredditId?: string
  ): Promise<unknown> {
    return this.repository.getCrawlHistory(hours, maxResults, page, pageSize, subredditId);
  }

  /**
   * SESSION_369: Get all regions for dropdown selection
   *
   * @returns Promise with list of regions with their Reddit source counts
   * @throws Error if fetch fails
   */
  async getRegions(): Promise<unknown> {
    return this.repository.getRegions();
  }

  /**
   * SESSION_369: Get Reddit sources for a specific region
   *
   * @param regionId - Region ID to filter by
   * @param isActive - Optional filter by active/inactive status
   * @returns Promise with list of Reddit sources for the region
   * @throws Error if fetch fails
   */
  async getRedditSourcesForRegion(
    regionId: string,
    isActive?: boolean
  ): Promise<unknown> {
    if (!this.isValidGuid(regionId)) {
      throw new Error('Invalid Region ID format');
    }
    return this.repository.getRedditSourcesForRegion(regionId, isActive);
  }

  /**
   * Validate GUID format
   * @param id - String to validate as GUID
   * @returns boolean - True if valid GUID format
   */
  private isValidGuid(id: string): boolean {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(id);
  }
}

/**
 * Default service instance
 */
export const adminOperationsService = new AdminOperationsService();

/**
 * Static API methods for use in React Query hooks
 * Sprint 027 SESSION_358: Added pagination and filter parameters
 * SESSION_369: Added regions and sources methods
 */
export const AdminOperationsApi = {
  getCrawlStatus: (): Promise<unknown> => {
    return adminOperationsService.getCrawlStatus();
  },
  getCrawlHistory: (
    hours: number = 24,
    maxResults: number = 50,
    page: number = 1,
    pageSize: number = 50,
    subredditId?: string
  ): Promise<unknown> => {
    return adminOperationsService.getCrawlHistory(hours, maxResults, page, pageSize, subredditId);
  },
  /**
   * SESSION_369: Get all regions for dropdown selection
   */
  getRegions: (): Promise<unknown> => {
    return adminOperationsService.getRegions();
  },
  /**
   * SESSION_369: Get Reddit sources for a specific region
   */
  getRedditSourcesForRegion: (
    regionId: string,
    isActive?: boolean
  ): Promise<unknown> => {
    return adminOperationsService.getRedditSourcesForRegion(regionId, isActive);
  }
};
